import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { Typography } from '@material-ui/core'
import { makeStyles, Theme } from '@material-ui/core/styles'
import TextField from '@material-ui/core/TextField'
import { KeyboardArrowDown } from '@material-ui/icons'
import Autocomplete from '@material-ui/lab/Autocomplete'
import clsx from 'clsx'
import React from 'react'

const elementPropertiesStyles = makeStyles((theme: Theme) => ({
    autoCompleteMargin: {
        '& .MuiFormControl-marginNormal': {
            marginTop: theme.spacing(1),
            marginBottom: theme.spacing(3),
        },
        '& .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"]': {
            padding: '4px 8px 4px 8px',
            borderRadius: '100px',
            border: '1px solid #CCCCCC',
        },
        '& .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"] .MuiAutocomplete-endAdornment': {
            right: '3px',
        },
    },
    autoCompletePopper: {
        '& .MuiAutocomplete-listbox': {
            overflowWrap: 'anywhere',
        },
    },
    listBox: {
        maxHeight: '250px',
        fontSize: pxToRem(13),
        fontFamily: 'Roboto',
        paddingLeft: '0',
    },
    inputText: {
        fontSize: pxToRem(13),
        padding: '0 0 0 0',
        color: '#314959',
    },
    elementPropertiesCont: {
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1),
        display: 'inline-flex',
        alignItems: 'center',
    },
    propertiesLabel: {
        color: '#314959',
        lineHeight: pxToRem(15.23),
        fontSize: pxToRem(13),
        fontFamily: 'Roboto',
        fontWeight: 600,
        marginRight: pxToRem(12),
        whiteSpace: 'pre',
    },
}))

/**
 * Props for the AutoCompleteElementProperties component.
 */
interface AutoCompleteElementPropertiesProps {
    /** The list of options to display in the dropdown. */
    dropdownList: string[]
    /** The name of the parameter associated with the autocomplete field. */
    parameterName: string
    /** The placeholder text to display in the input field. */
    placeholderText: string
    /** The currently selected value in the autocomplete field. */
    selectedValue: string | null
    /** Optional callback function triggered when the value changes. */
    handleChange?: (paramName: string, paramVal: string | null) => void
    /** Indicates whether the autocomplete field is disabled. */
    isDisabled: boolean
    /** Optional name attribute for the input field. */
    name?: string
    /** Optional custom CSS class or style for the component. */
    customizedStyle?: string
    /** The label text for the autocomplete field. */
    label: string
    /** The test ID for the component, used for testing purposes. */
    dataTestId: string
}
const ProjectPropertiesAutoCompleteWithLabel = ({
    dropdownList,
    parameterName,
    placeholderText,
    selectedValue,
    handleChange,
    isDisabled,
    name,
    customizedStyle,
    label,
    dataTestId,
}: AutoCompleteElementPropertiesProps) => {
    const classes = elementPropertiesStyles()
    return (
        <GridItem className={classes.elementPropertiesCont}>
            <Typography className={classes.propertiesLabel} variant="body2">
                {label}
            </Typography>
            <Autocomplete
                disabled={isDisabled}
                className={clsx(
                    classes.autoCompleteMargin,
                    customizedStyle && customizedStyle
                )}
                data-testid={
                    name ? `autoCompleteInput${name}` : 'autoCompleteInput'
                }
                options={dropdownList}
                classes={{ popper: classes.autoCompletePopper }}
                value={selectedValue!!}
                onChange={(
                    _: React.ChangeEvent<{}>,
                    inputValue: string | null
                ) => {
                    handleChange?.(parameterName, inputValue)
                }}
                disableClearable
                popupIcon={<KeyboardArrowDown fontSize="small" />}
                renderInput={(params) => (
                    <TextField
                        name={parameterName}
                        {...params}
                        variant="outlined"
                        placeholder={placeholderText}
                        inputProps={{
                            ...params.inputProps,
                            'aria-autocomplete': 'none',
                            autoComplete: 'chrome-off',
                            className: classes.inputText,
                            form: {
                                autocomplete: 'off',
                            },
                            'aria-label': parameterName,
                        }}
                        data-testid={dataTestId}
                        disabled={isDisabled}
                    />
                )}
                ListboxProps={{
                    className: classes.listBox,
                }}
            />
        </GridItem>
    )
}

export default ProjectPropertiesAutoCompleteWithLabel
